/**
 * Provenance Log — Records per-step execution details and data lineage.
 *
 * A ProvenanceLog is a standalone, JSON-serializable record of a single
 * workflow execution run. It captures timing, parameters, results, errors,
 * and data lineage (which step output fed which step input) for every step.
 *
 * Design principles:
 * - Standalone: not embedded in the Workflow object (see design.md D6)
 * - resultSummary stores only references (compound name/index, PDB ID),
 *   never full compound or protein data structures
 * - Serializes to a plain array of step records via toJSON()
 * - Round-trips losslessly: fromJSON(log.toJSON()) produces an equivalent log
 *
 * @module gibbs/workflow/provenance
 */

// ─── Step Record Shape ──────────────────────────────────────────────────────

/**
 * @typedef {Object} InputSource
 * @property {string} inputName - Name of the input on this step
 * @property {number} sourceStepIndex - Step index that produced the value
 * @property {string} outputName - Name of the output from the source step
 */

/**
 * @typedef {Object} StepRecord
 * @property {number} stepIndex - Position in execution order
 * @property {string} nodeId - Workflow node ID
 * @property {string} actionId - ActionDef ID
 * @property {Object} params - Parameters used for this execution
 * @property {number|null} startTime - Epoch ms when step started
 * @property {number|null} endTime - Epoch ms when step ended
 * @property {number|null} durationMs - Wall-clock duration in ms
 * @property {string|null} status - Final status (SUCCEEDED, FAILED, SKIPPED, CANCELLED)
 * @property {Object|null} resultSummary - Reference-only result summary
 * @property {InputSource[]} inputSources - Data lineage entries
 * @property {string|null} error - Error message if step failed
 */

// ─── ProvenanceLog Class ────────────────────────────────────────────────────

export class ProvenanceLog {
    constructor() {
        /** @type {Map<number, StepRecord>} */
        this.steps = new Map();
    }

    /**
     * Record the start of a step execution.
     *
     * @param {number} stepIndex - Position in topological execution order
     * @param {string} nodeId - Workflow node ID
     * @param {string} actionId - ActionDef ID
     * @param {Object} [params={}] - Parameters used for this execution
     */
    startStep(stepIndex, nodeId, actionId, params = {}) {
        this.steps.set(stepIndex, {
            stepIndex,
            nodeId,
            actionId,
            params,
            startTime: Date.now(),
            endTime: null,
            durationMs: null,
            status: null,
            resultSummary: null,
            inputSources: [],
            error: null,
        });
    }

    /**
     * Record the completion of a step execution.
     *
     * @param {number} stepIndex - Step index previously passed to startStep
     * @param {string} status - Final status (SUCCEEDED, FAILED, SKIPPED, CANCELLED)
     * @param {Object|null} [resultSummary=null] - Reference-only result summary
     * @param {string|null} [error=null] - Error message if failed
     * @throws {Error} If stepIndex has no matching startStep
     */
    endStep(stepIndex, status, resultSummary = null, error = null) {
        const record = this.steps.get(stepIndex);
        if (!record) {
            throw new Error(`No step record for stepIndex ${stepIndex} — call startStep() first`);
        }
        record.endTime = Date.now();
        record.durationMs = record.endTime - record.startTime;
        record.status = status;
        record.resultSummary = resultSummary;
        record.error = error;
    }

    /**
     * Record data lineage: which upstream step output fed this step's input.
     *
     * @param {number} stepIndex - The receiving step
     * @param {string} inputName - Name of the input on the receiving step
     * @param {number} sourceStepIndex - Step index that produced the value
     * @param {string} outputName - Name of the output from the source step
     * @throws {Error} If stepIndex has no matching startStep
     */
    addDataLineage(stepIndex, inputName, sourceStepIndex, outputName) {
        const record = this.steps.get(stepIndex);
        if (!record) {
            throw new Error(`No step record for stepIndex ${stepIndex} — call startStep() first`);
        }
        record.inputSources.push({ inputName, sourceStepIndex, outputName });
    }

    /**
     * Get the record for a specific step.
     *
     * @param {number} stepIndex
     * @returns {StepRecord|undefined}
     */
    getStep(stepIndex) {
        return this.steps.get(stepIndex);
    }

    /**
     * Get all step records in order.
     *
     * @returns {StepRecord[]}
     */
    getSteps() {
        return Array.from(this.steps.values()).sort((a, b) => a.stepIndex - b.stepIndex);
    }

    /** Number of recorded steps. */
    get stepCount() {
        return this.steps.size;
    }

    // ─── Serialization ─────────────────────────────────────────────────

    /**
     * Serialize the provenance log to a plain JSON-compatible array.
     *
     * @returns {StepRecord[]}
     */
    toJSON() {
        return this.getSteps().map((record) => ({
            stepIndex: record.stepIndex,
            nodeId: record.nodeId,
            actionId: record.actionId,
            params: record.params,
            startTime: record.startTime,
            endTime: record.endTime,
            durationMs: record.durationMs,
            status: record.status,
            resultSummary: record.resultSummary,
            inputSources: record.inputSources.map((s) => ({
                inputName: s.inputName,
                sourceStepIndex: s.sourceStepIndex,
                outputName: s.outputName,
            })),
            error: record.error,
        }));
    }

    /**
     * Reconstruct a ProvenanceLog from a serialized array.
     *
     * @param {StepRecord[] | string} json - Array of step records or JSON string
     * @returns {ProvenanceLog}
     * @throws {Error} If input is not an array
     */
    static fromJSON(json) {
        let data;
        if (typeof json === 'string') {
            try {
                data = JSON.parse(json);
            } catch (err) {
                throw new Error(`Failed to parse provenance JSON: ${err.message}`);
            }
        } else {
            data = json;
        }

        if (!Array.isArray(data)) {
            throw new Error('Provenance JSON must be an array of step records');
        }

        const log = new ProvenanceLog();

        for (const record of data) {
            log.steps.set(record.stepIndex, {
                stepIndex: record.stepIndex,
                nodeId: record.nodeId,
                actionId: record.actionId,
                params: record.params || {},
                startTime: record.startTime ?? null,
                endTime: record.endTime ?? null,
                durationMs: record.durationMs ?? null,
                status: record.status ?? null,
                resultSummary: record.resultSummary ?? null,
                inputSources: (record.inputSources || []).map((s) => ({
                    inputName: s.inputName,
                    sourceStepIndex: s.sourceStepIndex,
                    outputName: s.outputName,
                })),
                error: record.error ?? null,
            });
        }

        return log;
    }
}
